import { useRef, useState } from 'react'
import { AxiosError } from 'axios'

import CPFInput from '@/components/form/input/cpf-mask-input'
import { CEPInput } from '@/components/form/input/cep-mask-input'
import { WhatsAppInput } from '@/components/form/input'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { useClientContext } from '@/context/client'
import { useUtils } from '@/lib/hooks/utils'
import { toast } from '@/lib/hooks/use-toast'
import { RemovaCharEspeciais } from '@/utils/format'
import { useCheckOwnerByCpf } from '@/features/client/services'
import { ContadorForm } from './contador-form'
import { isValidPersonName } from '@/utils/format'

type AddressData = {
  cep: string
  logradouro: string
  bairro: string
  numero: string
  municipio: string
  estado: string
  complemento: string
}

const EMPTY_ADDRESS: AddressData = {
  cep: '',
  logradouro: '',
  bairro: '',
  numero: '',
  municipio: '',
  estado: '',
  complemento: ''
}

export function ClientPfIdentificationForm() {
  const {
    mostrarCamposContador,
    setClienteData,
    setMostrarCamposContador,
    setClientMode,
    setInformacoes,
    setInformacoesCnpj
  } = useClientContext()
  const contadorRef = useRef<HTMLDivElement>(null)
  const regimeRef = useRef<HTMLDivElement>(null)

  const [cpf, setCpf] = useState('')
  const [isCpfValid, setIsCpfValid] = useState(false)
  const [whatsapp, setWhatsapp] = useState('')
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [ownerLookupResult, setOwnerLookupResult] = useState<'idle' | 'existing' | 'new'>('idle')
  const [address, setAddress] = useState<AddressData>(EMPTY_ADDRESS)
  const [isCepLoading, setIsCepLoading] = useState(false)

  const { buscarCEP } = useUtils()
  const { mutateAsync: checkOwnerByCpf, isPending: isCheckingOwner } = useCheckOwnerByCpf()

  const baseFieldsAreValid = isCpfValid && whatsapp.trim() !== '' && email.trim() !== ''
  const addressFieldsAreValid =
    address.cep.trim() !== '' &&
    address.logradouro.trim() !== '' &&
    address.bairro.trim() !== '' &&
    address.numero.trim() !== '' &&
    address.municipio.trim() !== '' &&
    address.estado.trim() !== ''

  const requiresName = ownerLookupResult === 'new'
  const canContinue = baseFieldsAreValid && addressFieldsAreValid && (!requiresName || name.trim() !== '')

  const updateAddress = (field: keyof AddressData, value: string) => {
    setAddress(prev => ({ ...prev, [field]: value }))
  }

  const scrollToContador = () => {
    setTimeout(() => contadorRef.current?.scrollIntoView({ behavior: 'smooth' }), 100)
  }

  const handleContinuar = () => {
    setTimeout(() => regimeRef.current?.scrollIntoView({ behavior: 'smooth' }), 100)
  }

  const syncPfDataInContext = (mode: 'new-owner' | 'existing-owner') => {
    setClientMode(mode)
    setClienteData({
      name: mode === 'new-owner' ? name : '',
      whatsapp,
      email,
      cpf
    })

    const cleanCpf = RemovaCharEspeciais(cpf)
    const pfPayload = {
      cpf: cleanCpf,
      cnpjcpf: cleanCpf,
      nome: mode === 'new-owner' ? name : '',
      razao_social: mode === 'new-owner' ? name : cleanCpf,
      cep: address.cep,
      logradouro: address.logradouro,
      bairro: address.bairro,
      numero: address.numero,
      municipio: address.municipio,
      uf: address.estado,
      complemento: address.complemento,
      isPF: true
    }

    setInformacoes({
      razao_social: mode === 'new-owner' ? name : 'Pessoa Física',
      cnae: 'Pessoa Física',
      endereco: `${address.logradouro}, ${address.numero}, ${address.bairro}, ${address.estado}, ${address.cep}`
    })
    setInformacoesCnpj(pfPayload)
    setMostrarCamposContador(true)
    scrollToContador()
  }

  const handleCheckCpf = async () => {
    if (!baseFieldsAreValid) {
      toast({
        title: 'Preencha CPF, WhatsApp e e-mail corretamente.',
        variant: 'destructive'
      })
      return
    }

    try {
      const response = await checkOwnerByCpf(RemovaCharEspeciais(cpf))
      setOwnerLookupResult(response.exists ? 'existing' : 'new')
      setMostrarCamposContador(false)
      setClientMode(null)

      if (response.exists) {
        toast({
          title: 'CPF já vinculado ao tenant. Complete o endereço para continuar.'
        })
        return
      }

      toast({
        title: 'CPF não encontrado. Informe nome e endereço para continuar o cadastro.'
      })
    } catch (error) {
      const errorMessage =
        error instanceof AxiosError ? error.response?.data?.error || error.message : 'Erro ao verificar CPF.'

      toast({
        title: errorMessage,
        variant: 'destructive'
      })
    }
  }

  const handleCepChange = async (value: string) => {
    updateAddress('cep', value)
    const cleanCep = RemovaCharEspeciais(value)

    if (cleanCep.length !== 8) return

    setIsCepLoading(true)
    try {
      const response = await buscarCEP(cleanCep)
      if (!response) throw new Error('Nenhuma informação encontrada para este CEP.')

      setAddress(prev => ({
        ...prev,
        cep: response.cep || value,
        bairro: response.bairro || '',
        municipio: response.localidade || response.municipio || '',
        estado: response.uf || '',
        logradouro: response.logradouro || ''
      }))
    } catch (error) {
      toast({
        title: error instanceof Error ? error.message : 'Erro ao buscar CEP.',
        variant: 'destructive'
      })
    } finally {
      setIsCepLoading(false)
    }
  }

  const handleContinue = () => {
    if (!canContinue) {
      toast({
        title: 'Preencha todos os campos obrigatórios antes de continuar.',
        variant: 'destructive'
      })
      return
    }

    if (requiresName && !isValidPersonName(name)) {
      toast({
        title: 'Nome do proprietário não pode conter números.',
        variant: 'destructive'
      })
      return
    }

    syncPfDataInContext(ownerLookupResult === 'existing' ? 'existing-owner' : 'new-owner')
  }

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle>Cadastro de Pessoa Física</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <CPFInput
            value={cpf}
            onCPFChange={(value, valid) => {
              setCpf(value)
              setIsCpfValid(valid)
              setOwnerLookupResult('idle')
              setMostrarCamposContador(false)
              setClientMode(null)
            }}
          />

          <WhatsAppInput
            placeholder="WhatsApp"
            value={whatsapp}
            onChange={(value: string) => setWhatsapp(value)}
          />

          <Input placeholder="E-mail" value={email} type="email" onChange={e => setEmail(e.target.value)} />

          <Button onClick={handleCheckCpf} disabled={!baseFieldsAreValid || isCheckingOwner}>
            {isCheckingOwner ? 'Verificando CPF...' : 'Verificar CPF'}
          </Button>
        </div>

        {ownerLookupResult !== 'idle' && (
          <div className="space-y-4 rounded-lg border border-border p-4">
            {requiresName && (
              <Input
                placeholder="Nome completo da pessoa física"
                value={name}
                onChange={e => setName(e.target.value)}
              />
            )}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium">CEP</label>
                <CEPInput value={address.cep} onChange={handleCepChange} loading={isCepLoading} />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium">Número</label>
                <Input value={address.numero} onChange={e => updateAddress('numero', e.target.value)} />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium">Logradouro</label>
                <Input value={address.logradouro} onChange={e => updateAddress('logradouro', e.target.value)} />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium">Bairro</label>
                <Input value={address.bairro} onChange={e => updateAddress('bairro', e.target.value)} />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium">Município</label>
                <Input value={address.municipio} onChange={e => updateAddress('municipio', e.target.value)} />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium">UF</label>
                <Input value={address.estado} onChange={e => updateAddress('estado', e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium">Complemento</label>
                <Input value={address.complemento} onChange={e => updateAddress('complemento', e.target.value)} />
              </div>
            </div>

            <Button onClick={handleContinue} disabled={!canContinue}>
              Continuar cadastro
            </Button>
          </div>
        )}

        {mostrarCamposContador && (
          <div ref={contadorRef}>
            <ContadorForm onContinue={handleContinuar} />
          </div>
        )}

        <div ref={regimeRef} />
      </CardContent>
    </Card>
  )
}
